/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import cx from 'classnames';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useMutation, useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, TableContainer, Tooltip, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { Namespaces } from '../../../i18n';
import { OrganizationUserApiService } from '../../../services/OrganizationUserApiService';
import { RootState } from '../../../store';
import { useTableStyles } from '../../../styles/table';
import { ApiClientError } from '../../../types/api/ApiClientError';
import { ApiClientResponseData } from '../../../types/api/ApiClientResponse';
import { EntityId } from '../../../types/api/EntityId';
import { IOrganizationUser } from '../../../types/organization/IOrganizationUser';
import { fetchOrganizationUsers } from '../../../utils/queryFns';
import { reactToRemoveUserFromOrganization } from '../../../utils/reactions';
import { useApiValidation } from '../../../utils/useApiValidation';
import { useObjectState } from '../../../utils/useObjectState';
import { useOrganizationPermissions } from '../../../utils/useOrganizationPermissions';
import { useOrganizationRolesI18n } from '../../../utils/useOrganizationRolesI18n';
import { AlignCenter } from '../../AlignCenter/AlignCenter';
import { notifyReportableApiError } from '../../notifyReportableApiError';
import { SelectWithDescription } from '../../SelectWithDescription/SelectWithDescription';
import { SimpleConfirmModal } from '../../SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../SimpleNotifyModal/SimpleNotifyModal';
import { createMembersListStyles } from './OrganizationMembersList.styles';
import { UserAuthenticationInfo } from './UserAuthenticationInfo';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type OrganizationMembersListProps = PropsFromRedux & {
  organizationId: EntityId;
};

type State = {
  isOpenModal: boolean;
  isOpenErrorModal: boolean;
  selectedUser?: IOrganizationUser;
};

const OrganizationMembersListComponent = memo<OrganizationMembersListProps>(props => {
  const { organizationId, auth } = props;
  const classes = createMembersListStyles();
  const tableClasses = useTableStyles();
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const { errorMessage, setApiError } = useApiValidation();
  const permissions = useOrganizationPermissions(organizationId);
  const [state, setState] = useObjectState<State>({
    isOpenModal: false,
    isOpenErrorModal: false,
    selectedUser: undefined,
  });
  const { selectedUser, isOpenModal, isOpenErrorModal } = state;
  const { user } = auth;
  const organizationRoles = useOrganizationRolesI18n();

  const [deleteUser] = useMutation(OrganizationUserApiService.deleteUserFromOrganization, {
    onMutate: ({ organizationId, userId }) => {
      queryCache.cancelQueries([fetchOrganizationUsers.key, { organizationId }]);

      const queryData = queryCache.getQueryData<ApiClientResponseData<IOrganizationUser[]>>([
        fetchOrganizationUsers.key,
        { organizationId },
      ]);

      const previousUsers = queryData?.payload ?? [];

      const indexOf = previousUsers.findIndex(u => u.user.id === userId);

      if (indexOf > -1) {
        const preparedUsers = [...previousUsers];
        const userToDelete = preparedUsers.splice(indexOf, 1);
        queryCache.setQueryData([fetchOrganizationUsers.key, { organizationId }], {
          message: 'successful',
          payload: preparedUsers,
        } as ApiClientResponseData);
        return () => {
          const nextArray = [...preparedUsers];
          nextArray.splice(indexOf, 0, userToDelete[0]);
          queryCache.setQueryData([fetchOrganizationUsers.key, { organizationId }], {
            message: 'successful',
            payload: nextArray,
          } as ApiClientResponseData);
        };
      }
      return () => {};
    },
    onSettled: (data, error, { organizationId }) => {
      void reactToRemoveUserFromOrganization({ organizationId });
    },
    onError: (e, variables, rollback) => {
      const error = e as ApiClientError;
      if (error.response) {
        setState({ isOpenErrorModal: true });
        setApiError(error);
      }
      (rollback as () => any)();
    },
  });

  const membersQuery = useQuery([fetchOrganizationUsers.key, { organizationId }], fetchOrganizationUsers.fn);

  const members = useMemo(() => membersQuery.data?.payload ?? [], [membersQuery.data]);

  const onConfirmDelete = async () => {
    setState({ isOpenModal: false });
    try {
      await deleteUser({ organizationId, userId: selectedUser!.user.id });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <TableContainer>
        <Table aria-label="projects table" className={classes.root}>
          <TableHead>
            <TableRow>
              <TableCell className={cx(tableClasses.headerTableCell, classes.firstTableCell)}>
                {t('management.members_list.title_members')}
              </TableCell>
              <TableCell className={tableClasses.headerTableCell} colSpan={1}>
                {t('management.members_list.title_role')}
              </TableCell>
              <TableCell className={tableClasses.headerTableCell} colSpan={2}>
                {t('management.members_list.title_authentication')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map(member => (
              <TableRow key={member.id}>
                <TableCell
                  classes={{
                    root: cx(tableClasses.bodyTableCell, classes.firstTableCell),
                  }}
                >
                  <Typography component="p" variant="body2">
                    {member.user.firstName} {member.user.lastName}
                    {user?.id === member.user.id && (
                      <span className={classes.you}>({t('management.members_list.you')})</span>
                    )}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                  {permissions.edit_organization ? (
                    <SelectWithDescription
                      typographyVariant="body2"
                      disableUnderline
                      onSubmit={role =>
                        OrganizationUserApiService.updateUserRoleInOrganization(organizationId, member.user.id.trim(), {
                          role,
                        })
                          .then(() => membersQuery.refetch())
                          .catch(notifyReportableApiError)
                      }
                      value={member.role}
                      items={organizationRoles}
                      data-cy="members-tab-member-role"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      data-cy="members-tab-member-role"
                    >
                      <Typography variant="body2">
                        {organizationRoles.find(role => role.key === member.role)?.title}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  classes={{
                    root: cx(tableClasses.bodyTableCell, classes.firstTableCell),
                  }}
                >
                  <UserAuthenticationInfo user={member.user} />
                </TableCell>
                <TableCell align={'right'}>
                  {permissions.edit_organization && (
                    <Tooltip title={t(`${Namespaces.Cluster}:details.header.delete`)!}>
                      <IconButton
                        aria-label={t(`${Namespaces.Common}:label.remove_user_from_org`, {
                          id: member.id,
                        })}
                        onClick={() => {
                          setState({ isOpenModal: true, selectedUser: member });
                        }}
                        disabled={false}
                        size="small"
                        classes={{ root: classes.deleteIcon }}
                        data-cy="members-tab-remove-member"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!members.length && (
              <TableRow>
                <TableCell colSpan={2}>
                  <AlignCenter>
                    <Typography variant="body2" component="p">
                      {t('management.members_list.no_members')}
                    </Typography>
                  </AlignCenter>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <SimpleConfirmModal
        open={isOpenModal}
        message={t(`${Namespaces.Common}:confirmation.delete_user_from_organization`, {
          name: `${selectedUser?.user?.firstName} ${selectedUser?.user?.lastName}`,
        })}
        onCancel={() => setState({ isOpenModal: false })}
        onConfirm={onConfirmDelete}
      />
      <SimpleNotifyModal
        open={isOpenErrorModal}
        message={t([
          `${Namespaces.OrganizationAdmin}:users.action_update_role.${errorMessage}`,
          `${Namespaces.Common}:errors.unexpected_error`,
        ])}
        onOk={() => setState({ isOpenErrorModal: false })}
      />
    </>
  );
});

export const OrganizationMembersList = connector(OrganizationMembersListComponent);
